import { DataModuleVO } from './DataModuleVO';
import {
  DataAccountInfo,
  DataAssessorInfo,
  DataDateOfEntry,
  DataEntryAt,
  DataFinishedAt,
  DataFinishedDateTime,
  DataHostInfo,
  DataId,
  DataSessionInfo,
  DataStartDateTime,
  DataStartedAt,
  DataVORaw as DataVORaw0,
} from '../interface';
import {
  dataAccountInfo,
  dataAssessorInfo,
  dataDateOfEntry,
  dataFinishedDateTime,
  dataHostInfo,
  dataId,
  dataSessionInfo,
  dataStartDateTime,
} from '../schema';
import { Validate } from '@edgvr-front/util/validate';
import { WorkshopName, workshopName } from '@edgvr-front/workshops/domain';

export class DataVO {
  private static validation = Validate.compile(
    DataVO.validationSchema(),
    DataModuleVO
  );

  readonly startedAt: DataStartedAt;
  readonly finishedAt: DataFinishedAt;
  readonly entryAt: DataEntryAt;
  readonly assessor: string;
  readonly personsCount: number;

  constructor(private readonly value: DataVORaw) {
    Validate.assert(DataVO.validation, this.value);

    this.startedAt = new Date(
      this.value.StartDateTime
    ).getTime() as DataStartedAt;
    this.finishedAt = new Date(
      this.value.FinishedDateTime
    ).getTime() as DataFinishedAt;
    this.entryAt = new Date(this.value.DateOfEntry).getTime() as DataEntryAt;

    this.personsCount = this.value.ModuleStatistics.reduce((acc, item) => {
      item.persons.forEach((_) => acc.add(_.id));
      return acc;
    }, new Set<string>()).size;
    this.assessor = [
      this.value.AssessorInfo.Firstname,
      this.value.AssessorInfo.MiddleName,
      this.value.AssessorInfo.LastName,
    ].join(' ');
  }

  get workshopName() {
    return this.value.WorkshopName;
  }

  get id() {
    return this.value.uniqueId;
  }

  get modules() {
    return this.value.ModuleStatistics;
  }

  get accountId() {
    return this.value.AccountInfo.AccountID;
  }

  get accountName() {
    return this.value.AccountInfo.Company;
  }

  get centerName() {
    return this.value.AssessorInfo.CenterName;
  }

  get address() {
    return this.value.AccountInfo.Address;
  }

  get assessorFirstName() {
    return this.value.AssessorInfo.Firstname;
  }

  get assessorMiddleName() {
    return this.value.AssessorInfo.MiddleName;
  }

  get assessorLastName() {
    return this.value.AssessorInfo.LastName;
  }

  get hostDeviceId() {
    return this.value.HostInfo.HostDeviceID;
  }

  get hostDeviceModel() {
    return this.value.HostInfo.HostDeviceModel;
  }

  get hostDeviceVersion() {
    return this.value.HostInfo.HostVersion;
  }

  get sessionType() {
    return this.value.SessionInfo.SessionType;
  }

  get sessionDeviceSwaps() {
    return this.value.SessionInfo.DeviceSwaps;
  }

  toJSON() {
    return {
      ...this.value,
      ModuleStatistics: this.value.ModuleStatistics.map((_) => _.toJSON()),
    };
  }

  static factory(dataVORaw: DataVORaw0) {
    return new DataVO({
      ...dataVORaw,
      ModuleStatistics: dataVORaw.ModuleStatistics.map((_) =>
        DataModuleVO.factory(_)
      ),
    });
  }

  private static validationSchema() {
    return {
      type: 'object',
      required: [
        'WorkshopName',
        'ModuleStatistics',
        'StartDateTime',
        'AccountInfo',
        'AssessorInfo',
        'HostInfo',
        'SessionInfo',
        'FinishedDateTime',
        'uniqueId',
        'DateOfEntry',
      ],
      properties: {
        WorkshopName: workshopName,
        ModuleStatistics: {
          type: 'array',
          items: {
            instanceof: DataModuleVO.name,
          },
        },
        StartDateTime: dataStartDateTime,
        AccountInfo: dataAccountInfo,
        AssessorInfo: dataAssessorInfo,
        HostInfo: dataHostInfo,
        SessionInfo: dataSessionInfo,
        FinishedDateTime: dataFinishedDateTime,
        uniqueId: dataId,
        DateOfEntry: dataDateOfEntry,
      },
    };
  }
}

interface DataVORaw {
  WorkshopName: WorkshopName;
  ModuleStatistics: DataModuleVO[];
  StartDateTime: DataStartDateTime;
  AccountInfo: DataAccountInfo;
  AssessorInfo: DataAssessorInfo;
  HostInfo: DataHostInfo;
  SessionInfo: DataSessionInfo;
  FinishedDateTime: DataFinishedDateTime;
  uniqueId: DataId;
  DateOfEntry: DataDateOfEntry;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}
